import React, { useState } from 'react';
import Type from 'prop-types';
import { groupBy, noop } from 'lodash';
import {
  Dialog,
  DialogTitle,
  Typography,
  IconButton,
  ListSubheader,
  Divider,
} from '@mui/material';
import { Close as CloseIcon } from "@mui/icons-material";
import { useToggle } from 'react-use';
import { shortDateFullMonthFullYearFormat } from 'constants/dateFormats';
import { useTranslation } from 'hooks';
import { formatDate } from 'utils/date';
import FormattedDate from '../FormattedDate';
import Carousel from '../Carousel';
import styles from './ImageGallery.module.css';

const ImageGallery = (props) => {
  const {
    date,
    title,
    subtitle,
    images,
    rowHeight,
    columnCount,
    thumbnailWidth,
    thumbnailHeight,
    onThumbnailClick,
    withLightbox,
  } = props;
  const [isLightboxOpened, toggleLightbox] = useToggle(false);
  const [currentSlide, setSlide] = useState(0);
  const { t } = useTranslation();

  const handleThumbnailClick = (event) => {
    const slideId = Number(event.currentTarget.getAttribute('data-id'));
    onThumbnailClick(slideId);
    setSlide(slideId);

    toggleLightbox();
  };

  const handleSlideChange = (newSlide) => {
    setSlide(newSlide);
  };

  const renderImageDate = () => {
    const currentImageDate = images[currentSlide].createdAt;

    if (!currentImageDate) {
      return null;
    }

    return (
      <FormattedDate
        value={currentImageDate}
        token={shortDateFullMonthFullYearFormat}
        className={styles.date}
      >
        {date}
      </FormattedDate>
    );
  };

  const imagesByYear = groupBy(images, (element) =>
    new Date(element.createdAt).getFullYear(),
  );
  const yearsInReverseOrder = Object.keys(imagesByYear).sort(
    (a, b) => Number(b) - Number(a),
  );
  let imagesByMonth = {};
  yearsInReverseOrder.forEach((year) => {
    const imagesYearByMonth = groupBy(imagesByYear[year], (element) =>
      new Date(element.createdAt).getMonth(),
    );
    const orderedImagesYearByMonth = Object.keys(imagesYearByMonth)
      .sort((a, b) => Number(b) - Number(a))
      .reduce((obj, key) => {
        // eslint-disable-next-line no-param-reassign
        obj[
          `${formatDate(imagesYearByMonth[key][0].createdAt, 'LLLL')} ${year}`
        ] = imagesYearByMonth[key];
        return obj;
      }, {});
    imagesByMonth = Object.assign(imagesByMonth, orderedImagesYearByMonth);
  });
  // eslint-disable-next-line prefer-const
  let tilesProps = [];
  Object.entries(imagesByMonth).forEach(([titleGroup, imagesMonth]) => {
    tilesProps.push({
      key: `subheader ${titleGroup}`,
      'data-id': `subheader ${titleGroup}`,
      cols: columnCount,
      className: styles.gridlistSubheader,
      style: { height: 'auto' },
      children: (
        <>
          <ListSubheader component="div" className={styles.subheaderText}>
            {titleGroup}
          </ListSubheader>
          <Divider variant="middle" className={styles.divider} />
        </>
      ),
    });
    imagesMonth.forEach((image, idx) => {
      tilesProps.push({
        key: image.id,
        'data-id': idx,
        rows: thumbnailWidth,
        cols: thumbnailHeight,
        className: styles.gridlistTile,
        children: (
          <img
            src={image.url}
            alt={image.name}
            height={rowHeight}
            className={styles.img}
          />
        ),
        onClick: handleThumbnailClick,
      });
    });
  });

  return (
    <div>
      <div className={styles.root}>
        <div
          className={styles.gridlist}
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${columnCount}, 1fr)`,
            gridAutoRows: `${rowHeight}px`,
          }}
        >
          {tilesProps.map(({ key, cols = 1, rows = 1, style, children, ...tileProps }) => (
            <div
              key={key}
              {...tileProps}
              style={{
                gridColumn: `span ${cols}`,
                gridRow: `span ${rows}`,
                ...style,
              }}
            >
              {children}
            </div>
          ))}
        </div>
      </div>

      {withLightbox && (
        <Dialog
          classes={{ paper: styles.lightbox }}
          onClose={toggleLightbox}
          aria-labelledby={t('photoGallery')}
          open={isLightboxOpened}
          fullScreen
        >
          <IconButton
            classes={{ root: styles.lightboxCloseButton }}
            aria-label={t('close')}
            onClick={toggleLightbox}
          >
            <CloseIcon />
          </IconButton>

          <DialogTitle
            disableTypography
            classes={{ root: styles.lightboxHeader }}
          >
            <Typography
              variant="body1"
              classes={{ root: styles.lightboxTitle }}
              noWrap
            >
              {title}
            </Typography>
            <Typography
              variant="body2"
              classes={{ root: styles.lightboxSubtitle }}
              noWrap
            >
              {subtitle}
            </Typography>
          </DialogTitle>

          {renderImageDate()}

          <Carousel
            slides={images}
            currentSlide={currentSlide}
            onSlideChange={handleSlideChange}
            stepperProps={{ position: 'bottom', variant: 'text' }}
            stepperClassName={styles.lightboxStepper}
            slideClassName={styles.lightboxSlide}
            slideImgClassName={styles.lightboxSlideImg}
          />
        </Dialog>
      )}
    </div>
  );
};

ImageGallery.propTypes = {
  title: Type.string,
  subtitle: Type.string,
  date: Type.string,
  images: Type.arrayOf(
    Type.shape({
      id: Type.string.isRequired,
      name: Type.string.isRequired,
      url: Type.string.isRequired,
      createdAt: Type.string,
    }),
  ),
  rowHeight: Type.number,
  columnCount: Type.number,
  thumbnailWidth: Type.number,
  thumbnailHeight: Type.number,
  withLightbox: Type.bool,
  onThumbnailClick: Type.func,
};

ImageGallery.defaultProps = {
  title: null,
  subtitle: null,
  date: null,
  images: [],
  rowHeight: 120,
  columnCount: 3,
  thumbnailWidth: 1,
  thumbnailHeight: 1,
  withLightbox: true,
  onThumbnailClick: noop,
};

export default ImageGallery;
